import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './AuthContext';
import { useUserRole } from '../hooks/useUserRole';

interface FeatureFlags {
  bookingSystem: boolean;
  autoApproveUsers: boolean;
  notifications: boolean;
  coRiderSystem: boolean;
  publicProfiles: boolean;
  maintenanceMode: boolean;
}

interface FeatureFlagsContextType {
  features: FeatureFlags;
  updateFeature?: (feature: keyof FeatureFlags, enabled: boolean) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const DEFAULT_FEATURES: FeatureFlags = {
  bookingSystem: true,
  autoApproveUsers: false,
  notifications: true,
  coRiderSystem: true,
  publicProfiles: false,
  maintenanceMode: false
};

const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
  features: DEFAULT_FEATURES,
  isLoading: true,
  error: null
});

export const FeatureFlagsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { isManager } = useUserRole();
  const [features, setFeatures] = useState<FeatureFlags>(DEFAULT_FEATURES);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const initializeFeatures = async () => {
      try {
        const featuresRef = doc(db, 'settings', 'features');
        
        // Versuche zuerst das Dokument zu lesen
        const featuresDoc = await getDoc(featuresRef);
        
        if (!featuresDoc.exists()) {
          // Wenn das Dokument nicht existiert, erstelle es mit Standardwerten
          await setDoc(featuresRef, {
            ...DEFAULT_FEATURES,
            lastUpdated: new Date(),
            createdAt: new Date()
          });
        }

        // Setze den Echtzeit-Listener
        unsubscribe = onSnapshot(
          featuresRef,
          (doc) => {
            if (doc.exists()) {
              setFeatures({
                ...DEFAULT_FEATURES,
                ...doc.data()
              });
            } else {
              setFeatures(DEFAULT_FEATURES);
            }
            setError(null);
            setIsLoading(false);
          },
          (err) => {
            console.error('Error loading features:', err);
            setFeatures(DEFAULT_FEATURES); // Fallback zu Standardwerten bei Fehler
            setError('Fehler beim Laden der Funktionen');
            setIsLoading(false);
          }
        );
      } catch (err) {
        console.error('Error initializing features:', err);
        setFeatures(DEFAULT_FEATURES); // Fallback zu Standardwerten bei Fehler
        setError('Fehler beim Initialisieren der Funktionen');
        setIsLoading(false);
      }
    };

    // Starte die Initialisierung
    initializeFeatures();

    // Cleanup beim Unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const updateFeature = async (feature: keyof FeatureFlags, enabled: boolean) => {
    if (!isManager || !user) {
      throw new Error('Keine Berechtigung zum Ändern von Funktionen');
    }

    const featuresRef = doc(db, 'settings', 'features');

    try {
      await updateDoc(featuresRef, {
        [feature]: enabled,
        lastUpdated: new Date(),
        updatedBy: {
          userId: user.uid,
          timestamp: new Date(),
          action: `${feature} ${enabled ? 'enabled' : 'disabled'}`
        }
      });
    } catch (err) {
      console.error('Error updating feature:', err);
      throw new Error('Funktion konnte nicht aktualisiert werden');
    }
  };

  return (
    <FeatureFlagsContext.Provider value={{
      features,
      updateFeature: isManager ? updateFeature : undefined,
      isLoading,
      error
    }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => useContext(FeatureFlagsContext);